<template>
  <div class="inactive-monitoring-point">
    <v-text-field
      v-model="searchWord"
      dense
      clearable
      hide-details
      prepend-inner-icon="fa-search"
      class="inactive-monitoring-point__search"
      :label="$t('field.search_label')"
      :disabled="loading || candidates.length === 0"
      data-testid="inactive-monitoring-point-search"
    />

    <div
      v-if="loading"
      class="d-flex align-center justify-center mt-4"
      data-testid="inactive-monitoring-point-loading"
    >
      <v-progress-circular indeterminate color="primary" :size="24" />
      <span class="ml-2 text-body-2">{{ $t('msg.loading') }}</span>
    </div>
    <v-list
      v-else-if="filteredCandidates.length > 0"
      dense
      class="inactive-monitoring-point__list"
      data-testid="inactive-monitoring-point-list"
    >
      <v-list-item
        v-for="candidate in filteredCandidates"
        :key="candidate.id"
        :class="{ 'inactive-monitoring-point__item--selected': candidate.id === destinationId }"
        data-testid="inactive-monitoring-point-list-item"
        @click="select(candidate.id)"
      >
        <v-list-item-content class="min-w-0">
          <v-list-item-title class="text-truncate" :title="candidate.name">
            {{ candidate.name }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <p
      v-else-if="candidates.length > 0"
      class="mt-3 mb-0 inactive-monitoring-point__no-candidate"
      data-testid="inactive-monitoring-point-no-match"
    >
      {{ $t('msg.no_match') }}
    </p>
    <p
      v-else
      class="mt-3 mb-0 inactive-monitoring-point__no-candidate"
      data-testid="inactive-monitoring-point-no-candidate"
    >
      {{ $t('msg.no_candidate') }}
    </p>

    <p v-if="destinationId != null" class="mt-1 mb-0 inactive-monitoring-point__hint">
      {{ $t('msg.hint_destination') }}
    </p>
  </div>
</template>

<i18n lang="yaml">
ja:
  field:
    search_label: 'モニタリングポイント名で絞り込む'
  msg:
    loading: '移設先を読み込んでいます'
    no_candidate: '移設先に選べる休止中のモニタリングポイントがありません'
    no_match: '絞り込み条件に一致するモニタリングポイントがありません'
    hint_destination: '選んだモニタリングポイントは再び監視中になります'
en:
  field:
    search_label: 'Filter by Monitoring Point name'
  msg:
    loading: 'Loading destinations'
    no_candidate: 'There are no inactive Monitoring Points available as a destination'
    no_match: 'No Monitoring Points match the filter'
    hint_destination: 'The selected Monitoring Point will become active again'
</i18n>

<script>
export default {
  name: 'inactive-monitoring-point-list',
  inheritAttrs: false,
  props: {
    candidates: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchWord: '',
      destinationId: null,
    };
  },
  computed: {
    filteredCandidates() {
      const word = (this.searchWord || '').trim();
      if (word === '') {
        return this.candidates;
      }
      // 名前に英字を含むモニタリングポイントを大文字小文字の違いで取りこぼさないよう揃えて比較する
      const lowerWord = word.toLowerCase();
      return this.candidates.filter(candidate => candidate.name.toLowerCase().includes(lowerWord));
    },
  },
  methods: {
    select(id) {
      this.destinationId = id;
      this.$emit('change', id);
    },
  },
};
</script>

<style scoped lang="sass">
.inactive-monitoring-point
  // 入力文字は本文より一段小さくし、ダイアログ内の文字サイズの幅を抑える
  &__search
    ::v-deep input,
    ::v-deep .v-label
      font-size: 14px

    ::v-deep .v-input__prepend-inner .v-icon
      font-size: 16px

    // 絞り込み欄の下線と candidate 一覧の枠を隙間なく重ねる
    ::v-deep .v-input__slot
      margin-bottom: 0

  &__list
    max-height: 132px
    overflow-y: auto

    // v-list は theme から border-color: #fff を受け取るため、
    // クラスを重ねて詳細度を上げないと枠線が白のまま潰れる
    &.v-sheet.v-list
      border: 1px solid #dddddd
      // 直上の絞り込み欄と地続きに見せるため、下側だけ角を丸める
      border-radius: 0 0 4px 4px

  &__item--selected
    background-color: #e3f2fd

  // 候補なし・絞り込み一致なしの案内はリスト枠の外に置き、省略せず折り返す
  &__no-candidate
    font-size: 13px
    line-height: 1.6
    color: #888888

  &__hint
    font-size: 12px
    line-height: 1.6
    color: #888888
</style>
